// The localized-string widget (spec/dashboard.md section 6.4, "localized string"). In a form the text is the field: the
// default-language text of the linked string, edited in place (one staged `values.<lang>` change on the string), and on
// an empty field the typed text stages a new string with a derived key (`<OWNER>_<SLUG>_<PARAM>`, content/localization/
// keys.ts) that Save All sends through strings:batch before the documents (Q-D-25). The Localization options menu keeps
// the rest: choose an existing string (the combobox), a new string with all languages (the modal), open in Localization,
// unlink. A grid cell (`compact`) keeps the combobox over the group's strings (`KEY — text`) and None.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import type { Json, LocalizationString } from '../../api/types';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { Combobox } from '../../ui/Combobox';
import { Field } from '../../ui/Field';
import { ExternalLink, Languages, Plus, Search, Unlink } from '../../ui/icons';
import { Checkbox, CommitTextInput, TextArea, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';
import { deriveStringKey } from '../localization/keys';
import { createNamedString, editString, fullKeyOf, keysInGroup, valuesOf } from '../localization/strings';
import { useWidgetEnv, type WidgetProps } from './context';

/** "Type a name…", "Type a description…", else "Type text…" (by the parameter's name and display name). */
export function textPlaceholder(p: { name: string; displayName?: string | null }): string {
  const words = `${p.name} ${p.displayName ?? ''}`.toLowerCase();
  if (words.includes('description')) return 'Type a description…';
  if (words.includes('name')) return 'Type a name…';
  return 'Type text…';
}

export function LocalizedStringPicker({ param: p, value, onCommit, disabled, compact, id, owner }: WidgetProps) {
  const env = useWidgetEnv();
  const { catalog, workspace } = env;
  const version = useWorkspaceVersion(workspace);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [reset, setReset] = useState(0); // remounts the text input when a commit is refused (an emptied linked string)
  const chooser = useRef<HTMLSpanElement>(null);
  const group = p.group ?? null;
  const lang = catalog.defaultLanguage;
  const linkedId = typeof value === 'string' && value !== '' ? value : null;
  const row = linkedId ? ((workspace.getDraft(entityKey('string', linkedId))?.current as unknown as LocalizationString | undefined) ?? catalog.strings.get(linkedId)) : undefined;
  const options = useMemo(() => {
    const staged = workspace.all.filter((d) => d.kind === 'string' && d.status !== 'deleted').map((d) => d.current as unknown as LocalizationString);
    const all = [...staged, ...catalog.stringList].filter((s) => !group || s.group === group);
    return all
      .map((s) => ({ value: s.id, label: `${s.key} — ${catalog.stringText(s.id) ?? ''}`.replace(/ — $/, ''), hint: group ? undefined : s.group }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog, group, workspace, version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (choosing) chooser.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [choosing]);

  if (compact) {
    return (
      <span className="row">
        <Combobox id={id} size="s" value={linkedId} options={options} onChange={(v) => onCommit(v)} emptyLabel="None" placeholder={group ? `A string of ${group}…` : 'A localization string…'} disabled={disabled} aria-label={p.displayName ?? p.name} />
      </span>
    );
  }

  const text = row ? (valuesOf(row)[lang] ?? '') : '';
  const commitText = (next: string) => {
    if (row) {
      if (next.trim() === '') {
        setReset((n) => n + 1); // a shared string is never emptied from a field; Unlink clears the field
        return;
      }
      editString(workspace, catalog, row, `values.${lang}`, next);
      return;
    }
    const typed = next.trim();
    if (!typed) return;
    const targetGroup = group ?? catalog.groups[0] ?? 'DEFAULT';
    const ownerTemplate = owner ? catalog.resolve(owner)?.template : undefined;
    const key = deriveStringKey(ownerTemplate, typed, p.name, keysInGroup(workspace, catalog, targetGroup));
    const tempId = createNamedString(workspace, { group: targetGroup, key, values: { [lang]: typed }, export: true }, lang);
    onCommit(tempId);
  };
  const localizationHref = linkedId ? `${routes.branch(env.org, env.game, env.branch, 'localization/strings')}?string=${encodeURIComponent(linkedId)}` : null;
  const languageCount = row ? Object.values(valuesOf(row)).filter((v) => typeof v === 'string' && v.length > 0).length : 0;
  const label = p.displayName ?? p.name;

  return (
    <div className="lstring">
      <span className="row lstring-row">
        {choosing ? (
          <span
            ref={chooser}
            className="lstring-choose"
            onKeyDownCapture={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setChoosing(false);
              }
            }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setChoosing(false);
            }}
          >
            <Combobox
              id={id}
              value={linkedId}
              options={options}
              onChange={(v) => {
                setChoosing(false);
                onCommit(v);
              }}
              emptyLabel="None"
              placeholder={group ? `A string of ${group}…` : 'A localization string…'}
              aria-label={label}
            />
          </span>
        ) : (
          <CommitTextInput key={reset} id={id} className="lstring-text" value={text} onCommit={commitText} placeholder={textPlaceholder(p)} readOnly={disabled} aria-label={label} />
        )}
        <IconButton label="Localization options" aria-haspopup="menu" aria-expanded={menuAnchor !== null} disabled={disabled} onClick={(e) => setMenuAnchor(menuAnchor ? null : e.currentTarget)}>
          <Languages aria-hidden="true" />
        </IconButton>
      </span>
      {linkedId && (
        <span className="lstring-caption muted small">
          {row ? (
            <>
              {localizationHref ? <RouterLink to={localizationHref}>{fullKeyOf(row)}</RouterLink> : fullKeyOf(row)}
              {languageCount > 1 && <> · {languageCount} languages</>}
            </>
          ) : (
            <>A missing string ({linkedId}); typing creates a new one</>
          )}
        </span>
      )}
      <Menu
        anchor={menuAnchor}
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        label="Localization options"
        items={[
          { key: 'choose', label: 'Choose an existing string…', icon: <Search aria-hidden="true" />, onSelect: () => setChoosing(true) },
          { key: 'new', label: 'New string with all languages…', icon: <Plus aria-hidden="true" />, onSelect: () => setCreating(true) },
          ...(linkedId
            ? [
                { key: 'sep', label: '', separator: true },
                { key: 'open', label: 'Open in Localization', icon: <ExternalLink aria-hidden="true" />, onSelect: () => localizationHref && void navigate(localizationHref) },
                { key: 'unlink', label: 'Unlink', icon: <Unlink aria-hidden="true" />, onSelect: () => onCommit(null) },
              ]
            : []),
        ]}
      />
      {creating && (
        <NewStringModal
          group={group ?? catalog.groups[0] ?? 'DEFAULT'}
          onClose={() => setCreating(false)}
          onCreated={(tempId) => {
            setCreating(false);
            onCommit(tempId);
          }}
        />
      )}
    </div>
  );
}

/** "New localization string": Group, Key, Description, Export, one text per enabled language; staged, never saved here. */
export function NewStringModal({ group: initialGroup, onClose, onCreated }: { group: string; onClose: () => void; onCreated: (tempId: string) => void }) {
  const { catalog, workspace } = useWidgetEnv();
  const [group, setGroup] = useState(initialGroup);
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [exported, setExported] = useState(true);
  const [values, setValues] = useState<Record<string, string>>({});
  const keyError = !key ? null : /[\s/]/.test(key) ? 'No spaces or slashes in a key.' : catalog.stringList.some((s) => s.group === group && s.key === key) ? 'This key exists in the group.' : null;
  const canCreate = group.trim().length > 0 && key.trim().length > 0 && !keyError;
  const create = () => {
    const value = { id: '', group: group.trim(), key: key.trim(), description: description || null, export: exported, values } as unknown as Json;
    const tempId = workspace.create('string', value, { label: `${group.trim()}/${key.trim()}` });
    onCreated(tempId);
  };
  return (
    <Modal
      title="New localization string"
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} disabled={!canCreate}>
            Create
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Group" required>
          <>
            <TextInput list="new-string-groups" value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Group" />
            <datalist id="new-string-groups">
              {catalog.groups.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </>
        </Field>
        <Field label="Key" required error={keyError ?? undefined} help="The key inside the group; released as GROUP/KEY">
          <TextInput value={key} onChange={(e) => setKey(e.target.value)} aria-label="Key" autoFocus />
        </Field>
        <Field className="span-2" label="Description">
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" />
        </Field>
        <div className="field span-2">
          <Checkbox label="Exported (part of releases)" checked={exported} onChange={(e) => setExported(e.target.checked)} />
        </div>
        {catalog.languages.map((l) => (
          <Field key={l.code} className="span-2" label={`${l.name} (${l.code})`}>
            <TextArea rows={2} value={values[l.code] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [l.code]: e.target.value }))} aria-label={`Text in ${l.name}`} />
          </Field>
        ))}
        <p className="muted small span-2">The string is staged with your other changes; Save All creates it before the documents that use it.</p>
      </div>
    </Modal>
  );
}
